import { LessonResponse } from '@mnspro-cloud/services/lib/mnspro-cloud/api/v2';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../../Button';
import { ResponsiveDialog } from '../../../ResponsiveDialog/ResponsiveDialog';
import { MinutesInput } from './MinutesInput';

export interface StudentLeftEarlyDialogProps {
  open: boolean;
  studentDisplayName: string;
  minutes?: number;
  onCloseClick: () => void;
  onSaveClick: (minutes: number) => void;
  lesson?: LessonResponse;
}

export const StudentLeftEarlyDialog: React.FC<
  StudentLeftEarlyDialogProps
> = props => {
  const [minutes, setMinutes] = useState<number>(0);
  const [arrivedNow, setArrivedNow] = useState<boolean>(false);
  const { t } = useTranslation();

  useEffect(() => {
    if (props.minutes) {
      setMinutes(props.minutes);
    }
  }, [props]);

  const lessonDurationMins: number | undefined = useMemo(() => {
    if (!props.lesson) {
      return undefined;
    }

    const duration = moment.duration(
      moment(props.lesson.end).diff(props.lesson.start),
    );

    return duration.asMinutes();
  }, [props.lesson]);

  const isValid = useMemo(() => {
    if (minutes < 0) {
      return false;
    }

    if (lessonDurationMins !== undefined) {
      return minutes < lessonDurationMins;
    }

    return true;
  }, [lessonDurationMins, minutes]);

  return (
    <ResponsiveDialog
      open={props.open}
      header={`${t('classbook:absences:missed_minutes', { defaultValue: 'Verpasste Minuten von' })} ${props.studentDisplayName}`}
      className="studentLeftEarlyDialog"
      thirdAction={
        <Button
          onClick={() => {
            props.onCloseClick();
            setMinutes(0);
          }}
        >
          {t('cancel', { defaultValue: 'Abbrechen' })}
        </Button>
      }
      primaryAction={
        <Button
          appearance="primary"
          onClick={() => {
            props.onSaveClick(minutes);
            setMinutes(0);
          }}
          disabled={!isValid}
        >
          {t('assume', { defaultValue: 'Übernehmen' })}
        </Button>
      }
    >
      <MinutesInput
        minutes={minutes}
        max={
          lessonDurationMins
            ? Math.max(lessonDurationMins - 1, 0)
            : undefined
        }
        minutesChanged={minutes => {
          setMinutes(minutes);
          setArrivedNow(false);
        }}
        arrivedNow={
          props.lesson &&
          moment().isBetween(
            moment(props.lesson.start),
            moment(props.lesson.end),
          )
            ? () => {
                const start = moment(props.lesson?.start ?? '');
                const now = moment();
                setMinutes(
                  Math.round(moment.duration(now.diff(start)).asMinutes()),
                );
                setArrivedNow(true);
              }
            : undefined
        }
      />
      {props.lesson &&
        moment().isBetween(
          moment(props.lesson.start),
          moment(props.lesson.end),
        ) &&
        arrivedNow && (
          <span>
            {`${t('classbook:absences:missed_minutes_sum', { defaultValue: 'Verpasste Minuten:' })} ${minutes}`}
          </span>
        )}
    </ResponsiveDialog>
  );
};
